/**
 * "Add records": file any kind of record into this folder from the folder itself.
 *
 * WHAT IS OFFERED IS WHAT THE SERVER WOULD ACCEPT (folder.find_records_to_file): records of any type that the active
 * persona can see and that the caller holds the edit privilege on -- Ed's rule of 2026-11-03, "users must have a
 * privilege grant for any given object" -- and that are not in this folder already. The write asks both again.
 *
 * A RECORD LIVES IN ONE FOLDER, so adding one that is filed elsewhere moves it; each row says where it is now.
 */
import React, {useEffect, useState} from "react";
import {Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment,
        TextField, Typography} from "@mui/material";
import {SearchOutlined} from "@mui/icons-material";
import {classificationByCode} from "../../util/objectClassifications";
import {Grey5, Grey7, Grey17} from "../../theme/styles";

type Row = Record<string, any>;

export default function AddRecordsDialog({folder, api, onClose, onAdded}: {
    folder: Row; api: any; onClose: () => void; onAdded: () => void;
}) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<Row[] | null>(null);
    const [chosen, setChosen] = useState<Record<string, Row>>({});
    const [saving, setSaving] = useState(false);
    const [problems, setProblems] = useState<string[]>([]);

    // ASKED AS THE READER TYPES, a moment after they stop, so a search is not sent per keystroke.
    useEffect(() => {
        let current = true;
        setResults(null);
        const timer = window.setTimeout(() => {
            api.get("t1/v1/folders", {params: {USID: folder.USID, View: "fileable", Query: query.trim() || undefined}})
                .then((answer: any) => { if (current) setResults(answer?.data?.Results ?? []); })
                .catch((failure: any) => {
                    if (!current) return;
                    setResults([]);
                    setProblems([failure?.response?.data?.Cause ?? "The records could not be searched."]);
                });
        }, 300);
        return () => { current = false; window.clearTimeout(timer); };
    }, [api, folder.USID, query]);

    const toggle = (row: Row) => setChosen(was => {
        const next = {...was};
        if (next[row.ObjectUSID]) delete next[row.ObjectUSID]; else next[row.ObjectUSID] = row;
        return next;
    });

    const add = async () => {
        setSaving(true);
        const failed: string[] = [];
        for (const row of Object.values(chosen)) {
            try {
                await api.post("t1/v1/folder_records", {FolderUSID: folder.USID, ObjectRevisionUSID: row.RevisionUSID});
            } catch (failure: any) {
                failed.push(`${row.ObjectID ?? row.Name}: ${failure?.response?.data?.Cause ?? "could not be added"}`);
            }
        }
        setSaving(false);
        if (failed.length) { setProblems(failed); onAdded(); return; }
        onAdded();
        onClose();
    };

    const count = Object.keys(chosen).length;
    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>Add records to “{folder.Name}”</DialogTitle>
            <DialogContent dividers sx={{minHeight: 380}}>
                <TextField autoFocus fullWidth size="small" placeholder="Search by number or name" value={query}
                           onChange={event => setQuery(event.target.value)}
                           slotProps={{input: {startAdornment: <InputAdornment position="start"><SearchOutlined sx={{fontSize: 18}}/></InputAdornment>}}}/>
                <Typography sx={{fontSize: 12, color: Grey5, mt: 1}}>
                    Any record you can see and hold the edit privilege on. A record lives in one folder, so one filed
                    elsewhere moves here.
                </Typography>
                <Box sx={{mt: 1.5}}>
                    {results === null ? <CircularProgress size={20} sx={{m: 2}}/> : results.length === 0
                        ? <Typography sx={{fontSize: 13, color: Grey7, p: 1}}>No records you could add match.</Typography>
                        : results.map(row => {
                            const Icon = classificationByCode(row.Classification)?.icon;
                            return (
                                <Box key={row.ObjectUSID} onClick={() => toggle(row)}
                                     sx={{display: "flex", alignItems: "center", gap: 1, py: 0.5, px: 0.5, cursor: "pointer",
                                          borderRadius: 1, "&:hover": {backgroundColor: "#F5F7FA"}}}>
                                    <Checkbox size="small" checked={!!chosen[row.ObjectUSID]} sx={{p: 0.5}}/>
                                    {Icon ? <Icon sx={{fontSize: 18, color: Grey7}}/> : null}
                                    <Box sx={{minWidth: 0, flex: 1}}>
                                        <Typography sx={{fontSize: 13.5, color: Grey17}} noWrap>
                                            {row.ObjectID ? `${row.ObjectID} · ` : ""}{row.Name}
                                        </Typography>
                                        <Typography sx={{fontSize: 12, color: Grey5}}>
                                            {classificationByCode(row.Classification)?.singular ?? row.Classification}
                                            {row.Status ? ` · ${String(row.Status).toLowerCase()}` : ""}
                                            {row.FiledIn ? ` · now in ${row.FiledIn}` : ""}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                </Box>
                {problems.map(one => <Typography key={one} sx={{fontSize: 12.5, color: "#B3261E", mt: 1}}>{one}</Typography>)}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button variant="contained" disabled={!count || saving} onClick={add}>
                    {saving ? "Adding…" : count ? `Add ${count} record${count === 1 ? "" : "s"}` : "Add"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
